import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe2, Plus, MapPin, Users, Clock, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fileUrl } from "@/lib/api";

export default function Communities({ embedded = false }) {
  const navigate = useNavigate();
  const [towns, setTowns] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", region: "", description: "", photo: null });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => { try { setTowns((await api.get("/towns")).data); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((s) => ({ ...s, photo: data.url })); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/towns", form); toast.success("Request sent! An admin will review and approve your town page."); setShow(false); setForm({ name: "", region: "", description: "", photo: null }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const mine = (towns || []).filter((t) => t.my_status === "approved" || t.is_town_admin);
  const discover = (towns || []).filter((t) => !(t.my_status === "approved" || t.is_town_admin));

  return (
    <div className={embedded ? "" : "max-w-4xl mx-auto"}>
      <div className="flex items-center gap-3 mb-2">
        {!embedded && <div className="w-12 h-12 rounded-xl bg-forest-700 flex items-center justify-center"><Globe2 size={26} strokeWidth={2.5} className="text-white" /></div>}
        <div className="flex-1">
          {!embedded && <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900">Communities</h1>}
          <p className={embedded ? "text-lg text-forest-700" : "text-forest-500"}>Connect with your hometown — reach residents at home and family abroad{embedded ? "." : ""}</p>
        </div>
        <button onClick={() => setShow((v) => !v)} data-testid="request-town-btn" className="min-h-[52px] px-5 rounded-full bg-terracotta text-white font-bold flex items-center gap-2 hover:bg-terracotta-dark"><Plus size={20} strokeWidth={2.5} /> Request a town</button>
      </div>

      {show && (
        <form onSubmit={submit} className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 my-5" data-testid="town-form">
          <h2 className="font-head text-xl font-bold text-forest-900 mb-4">Request a new town page</h2>
          <div className="space-y-3">
            <input data-testid="town-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Town name (e.g. Begowal)" className="w-full min-h-[52px] p-3 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream" />
            <input data-testid="town-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Region (e.g. Punjab, India)" className="w-full min-h-[52px] p-3 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream" />
            <textarea data-testid="town-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Tell us about your town" className="w-full p-3 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream resize-none" />
            <div className="flex items-center gap-3">
              {form.photo && <img src={fileUrl(form.photo)} alt="" className="w-16 h-16 rounded-xl object-cover border-2 border-border" />}
              <label className="min-h-[48px] px-4 rounded-full border-2 border-border font-semibold text-forest-700 flex items-center gap-2 cursor-pointer hover:border-forest-500">
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} strokeWidth={2.5} />} {form.photo ? "Change photo" : "Add a photo"}
                <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="town-photo" />
              </label>
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="town-submit" className="w-full min-h-[52px] mt-4 rounded-full bg-terracotta text-white font-bold hover:bg-terracotta-dark disabled:opacity-60">{busy ? "Sending…" : "Submit for approval"}</button>
        </form>
      )}

      {towns === null ? <div className="h-32 rounded-2xl bg-white/60 border-2 border-border animate-pulse mt-5" /> : (
        <div className="mt-6 space-y-6">
          {mine.length > 0 && <Section title="Your communities" towns={mine} navigate={navigate} testid="my-towns" />}
          <Section title="Discover towns" towns={discover} navigate={navigate} testid="discover-towns" empty="No town pages yet. Be the first to request one!" />
        </div>
      )}
    </div>
  );
}

function Section({ title, towns, navigate, testid, empty }) {
  return (
    <div data-testid={testid}>
      <h2 className="font-head text-2xl font-bold text-forest-900 mb-3">{title}</h2>
      {towns.length === 0 ? <p className="text-forest-700 bg-white rounded-2xl border-2 border-border p-6">{empty}</p> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {towns.map((t) => (
            <button key={t.town_id} onClick={() => navigate(`/app/communities/${t.town_id}`)} data-testid={`town-card-${t.town_id}`} className="bg-white rounded-2xl border-2 border-border shadow-sm overflow-hidden text-left hover:-translate-y-1 transition-transform">
              {t.photo ? <img src={fileUrl(t.photo)} alt={t.name} className="w-full h-36 object-cover" /> : <div className="w-full h-36 bg-forest-700 flex items-center justify-center"><Globe2 size={40} className="text-white/50" /></div>}
              <div className="p-4">
                <p className="font-head text-xl font-bold text-forest-900">{t.name}</p>
                {t.region && <p className="text-forest-500 flex items-center gap-1 text-sm"><MapPin size={14} strokeWidth={2.5} />{t.region}</p>}
                <p className="text-forest-700 text-sm mt-2 line-clamp-2">{t.description}</p>
                <div className="flex items-center gap-3 mt-3 text-sm font-semibold">
                  <span className="text-forest-500 flex items-center gap-1"><Users size={15} strokeWidth={2.5} />{t.member_count}</span>
                  {t.my_status === "pending" && <span className="text-gold-DEFAULT text-gold flex items-center gap-1"><Clock size={15} strokeWidth={2.5} /> Pending</span>}
                  {(t.my_status === "approved" || t.is_town_admin) && <span className="text-forest-700">{t.is_town_admin ? "Admin" : "Member"}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
